import {
  BookOpenText,
  Inbox,
  MessagesSquare,
  Search,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

/**
 * The phone app's five tabs, in the handoff's order:
 * World view · Needs you · Ask · Search · People.
 *
 * Each one is a route the desktop shell already has, not a phone-only page id. That is
 * what keeps a link, a manifest shortcut and a deep link meaning the same thing on both
 * surfaces — the phone app is a second chrome, never a second app.
 *
 * The rail's ten destinations are deliberately not five: Recordings, Insights, Entities and
 * Assistant have no phone design and are not improvised into one (see `DESKTOP_ONLY`).
 */
export interface PhoneTab {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
  /** Whether the rail's pending count is shown here too. Only Needs you carries one. */
  badged?: boolean;
}

export const PHONE_TABS: PhoneTab[] = [
  { id: "world", path: "/world-view", label: "World view", icon: BookOpenText },
  { id: "needs", path: "/review", label: "Needs you", icon: Inbox, badged: true },
  { id: "ask", path: "/chat", label: "Ask", icon: MessagesSquare },
  { id: "search", path: "/search", label: "Search", icon: Search },
  { id: "people", path: "/people", label: "People", icon: UsersRound },
];

/** Where the phone app starts, and where an address it does not serve sends somebody. */
export const PHONE_HOME = "/world-view";

export interface PhoneChrome {
  /** The header's title. `null` when the shell has to name it from data (a subject). */
  title: string | null;
  /** Which tab lights up, or null on a page that is not one (a drill-down, or Add). */
  tab: string | null;
  /** Where the header's back button goes, or null when there is no back button. */
  back: string | "last-tab" | null;
  /** The handoff shows the add button on World view and Needs you only. */
  showAdd: boolean;
}

/**
 * What the phone chrome says about one address.
 *
 * Derived from the path for the same reason `useActiveSurface` derives the desktop chrome
 * from it: a page cannot forget to describe itself, and a cold deep link has the whole
 * header and the right tab before the page has rendered anything.
 */
export function phoneChrome(pathname: string): PhoneChrome {
  if (pathname.startsWith("/world-view/")) {
    return { title: null, tab: null, back: "/world-view", showAdd: false };
  }

  if (pathname.startsWith("/add")) {
    // Entered from World view or from Needs you, and the handoff's back button returns to
    // whichever it was — so the target is the shell's, not this table's.
    return { title: "Add a source", tab: null, back: "last-tab", showAdd: false };
  }

  // The one Settings section the phone app draws (CAP-5), entered from Add a source —
  // which is where somebody is standing when they wonder how a meeting gets in, and so
  // where its back button returns to.
  if (pathname.startsWith("/settings/capture")) {
    return { title: "Capture PC", tab: null, back: "/add", showAdd: false };
  }

  // Before the tab match, because `/people/<id>` is a dossier and not the gallery — and a
  // drill-down the phone app does not serve must not light the tab it hangs under.
  const elsewhere = desktopOnly(pathname);
  if (elsewhere) return { title: elsewhere.name, tab: null, back: "last-tab", showAdd: false };

  const tab = PHONE_TABS.find((candidate) => pathname.startsWith(candidate.path));
  if (tab) {
    return {
      title: tab.label,
      tab: tab.id,
      back: null,
      showAdd: tab.id === "world" || tab.id === "needs",
    };
  }

  return { title: "Factorium", tab: null, back: "last-tab", showAdd: false };
}

/**
 * What a desktop-only address is called, so the notice can name the screen rather than
 * saying "this page". The list is exactly the rail destinations and surfaces the phone
 * handoff does not draw — the ones it does draw are above, and anything new lands here
 * loudly rather than rendering a desktop layout at 390 px.
 */
const DESKTOP_ONLY: { prefix: string; name: string; why: string }[] = [
  {
    prefix: "/recordings",
    name: "Recordings",
    why: "Following a recording through the pipeline, and reading its transcript, are desk work.",
  },
  {
    prefix: "/insights",
    name: "Insights",
    why: "The ledger is a table of horizons, verdicts and funnel counts — it needs the width.",
  },
  {
    prefix: "/entities",
    name: "Entities",
    why: "A sortable, filterable table of everything named. Same reason.",
  },
  {
    prefix: "/assistant",
    name: "Assistant",
    why: "Recaps and meeting prep sit beside a calendar, which the phone app has no room for.",
  },
  {
    prefix: "/people/",
    name: "This dossier",
    why: "Full dossiers, voiceprints and speaker matching stay on the desktop.",
  },
  {
    prefix: "/sources/",
    name: "This source",
    why: "A source opens onto its pipeline, its transcript and its insights — desk work.",
  },
  { prefix: "/settings", name: "Settings", why: "Every setting here is set up once, from a desk." },
  {
    prefix: "/processing",
    name: "Insight processing",
    why: "A developer view of a synthesis batch — prompts, artifacts and a funnel of counts. Desk work, and developer-only besides.",
  },
];

/** The desktop-only screen this address is, or null when the phone app serves it. */
export function desktopOnly(pathname: string): { name: string; why: string } | null {
  // UI-12, named before the recordings prefix would claim it. M12 says it stays desktop-only
  // and this is the sentence that says why, rather than the list's answer about recordings.
  if (pathname.endsWith("/review") && pathname.startsWith("/recordings/")) {
    return {
      name: "Transcript Review",
      why: "Word-level playback, a scrubbable waveform and inline correction over an hour of speech. It needs a keyboard and a window.",
    };
  }

  // Registering the PC that runs Factorium Recorder, named before the `/settings` prefix
  // would claim it. It is the one step under Settings that is a thing to *do* rather than
  // configuration set up once, and it is asked for while standing at that PC — so the
  // phone app serves it (`PhoneCaptureToken`), and the rest of Settings stays desk work.
  if (pathname.startsWith("/settings/capture")) return null;

  const match = DESKTOP_ONLY.find((candidate) => pathname.startsWith(candidate.prefix));
  return match ? { name: match.name, why: match.why } : null;
}
